import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import axiosInstance from '../config/axios';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';
import {
  buildBulkExportSitesParams,
  loadBulkExportManagers,
  toggleIdInSelection,
  selectAllAvailableSiteIds,
  countSelectedAvailable,
  validateBulkExportDateRange
} from '../utils/fullInventoryBulkExportUi';

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * Admin-only panel: export full inventory counts in a date range as a ZIP.
 */
const BulkExportFullCounts = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [managers, setManagers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedManagerIds, setSelectedManagerIds] = useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [managersLoading, setManagersLoading] = useState(true);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportInFlight = useRef(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const dateValidation = validateBulkExportDateRange(startDate, endDate);

  useEffect(() => {
    let cancelled = false;
    loadBulkExportManagers({
      request: (...args) => axiosInstance.get(...args),
      isCancelled: () => cancelled,
      setLoading: setManagersLoading,
      setManagers,
      setError
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedManagerIds.length) {
      setSites([]);
      setSelectedSiteIds([]);
      setSitesLoading(false);
      return undefined;
    }
    if (!startDate || !endDate || dateValidation) {
      setSites([]);
      setSelectedSiteIds([]);
      setSitesLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setSitesLoading(true);
    const requestTimer = window.setTimeout(async () => {
      setError('');
      setSuccess('');
      try {
        const params = buildBulkExportSitesParams(selectedManagerIds, startDate, endDate);
        const res = await axiosInstance.get('/api/tenant/reports/full-inventory/bulk-export/sites', {
          params,
          signal: controller.signal
        });
        if (!cancelled) {
          const nextSites = res.data?.sites || [];
          setSites(nextSites);
          setSelectedSiteIds((prev) =>
            prev.filter((id) => nextSites.some((s) => s.siteId === id && s.countAvailable))
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            getFriendlyErrorMessage(err, { kind: 'load' }) ||
              'We could not load sites for the selected Area Managers. Please try again.'
          );
          setSites([]);
          setSelectedSiteIds([]);
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(requestTimer);
      controller.abort();
    };
  }, [selectedManagerIds, startDate, endDate, dateValidation]);

  const availableSites = useMemo(() => sites.filter((s) => s.countAvailable), [sites]);
  const unavailableSites = useMemo(() => sites.filter((s) => !s.countAvailable), [sites]);
  const selectedAvailableCount = useMemo(
    () => countSelectedAvailable(sites, selectedSiteIds),
    [sites, selectedSiteIds]
  );

  const canExport =
    selectedManagerIds.length > 0 &&
    selectedAvailableCount > 0 &&
    !!startDate &&
    !!endDate &&
    !dateValidation &&
    !sitesLoading &&
    !exporting;

  const onToggleManager = (managerId) => {
    setSelectedManagerIds((prev) => toggleIdInSelection(prev, managerId));
    setSuccess('');
  };

  const onToggleSite = (siteId, available) => {
    if (!available) return;
    setSelectedSiteIds((prev) => toggleIdInSelection(prev, siteId));
    setSuccess('');
  };

  const onSelectAllSites = () => {
    setSelectedSiteIds(selectAllAvailableSiteIds(sites));
    setSuccess('');
  };

  const onClearSites = () => {
    setSelectedSiteIds([]);
    setSuccess('');
  };

  const exportSelected = useCallback(async () => {
    if (!canExport || exportInFlight.current) return;
    exportInFlight.current = true;
    setExporting(true);
    setError('');
    setSuccess('');
    try {
      const res = await axiosInstance.post(
        '/api/tenant/reports/full-inventory/bulk-export',
        {
          managerIds: selectedManagerIds,
          siteIds: selectedSiteIds,
          selectAllSites: false,
          startDate,
          endDate
        },
        { responseType: 'blob' }
      );
      const blob = res.data;
      if (blob && blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        let parsed = null;
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = null;
        }
        throw Object.assign(new Error(parsed?.userMessage || parsed?.error || 'Export failed'), {
          response: { data: parsed, status: res.status }
        });
      }
      const disposition = res.headers['content-disposition'] || '';
      let filename = 'FullInventoryCounts.zip';
      const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(disposition);
      if (match) {
        filename = decodeURIComponent(match[1] || match[2] || filename);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const exported = res.headers['x-exported-count'];
      const skipped = res.headers['x-skipped-count'];
      const countTabs = res.headers['x-count-tabs'];
      setSuccess(
        `Export ready. ${exported != null ? exported + ' site file(s)' : 'ZIP'} downloaded` +
          (countTabs != null ? ` with ${countTabs} count tab(s)` : '') +
          (skipped && Number(skipped) > 0 ? ` (${skipped} skipped — see Export Summary.txt in the ZIP).` : '.')
      );
    } catch (err) {
      setError(
        getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE
      );
    } finally {
      exportInFlight.current = false;
      setExporting(false);
    }
  }, [canExport, selectedManagerIds, selectedSiteIds, startDate, endDate]);

  return (
    <div className="card fir-filters-card fir-bulk-export-card">
      <div className="fir-bulk-collapsed-header">
        <div>
          <h3 className="fir-filters-card-title">Bulk Export Full Counts</h3>
          <p className="fir-filters-card-subtitle">
            Export full inventory counts for multiple sites as one ZIP file.
          </p>
        </div>
        <button
          type="button"
          className="fir-bulk-toggle"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
          aria-controls="fir-bulk-export-content"
        >
          {isExpanded ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
          {isExpanded ? 'Hide bulk export' : 'Show bulk export'}
        </button>
      </div>

      {isExpanded && (
        <div id="fir-bulk-export-content" className="fir-bulk-content">
          <div className="fir-bulk-content-actions">
            <button
              type="button"
              className="fir-btn-export-outline"
              onClick={exportSelected}
              disabled={!canExport}
              title="Export selected sites"
            >
              <Download size={16} aria-hidden />
              {exporting ? 'Exporting…' : 'Export selected sites'}
            </button>
          </div>

          {error && (
            <div className="error fir-alert" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="fir-bulk-success" role="status">
              {success}
            </div>
          )}

          <div className="fir-bulk-date-range">
            <div className="fir-filter-group">
              <label htmlFor="fir-bulk-start-date">Start date</label>
              <input
                id="fir-bulk-start-date"
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  setSuccess('');
                }}
              />
            </div>
            <div className="fir-filter-group">
              <label htmlFor="fir-bulk-end-date">End date</label>
              <input
                id="fir-bulk-end-date"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(event) => {
                  setEndDate(event.target.value);
                  setSuccess('');
                }}
              />
            </div>
            <p className="fir-bulk-date-help">
              Counts within this date range will be exported. If a site has multiple counts in the
              range, each count will be placed on a separate worksheet in that site's workbook.
            </p>
          </div>
          {dateValidation && (
            <div className="error fir-alert" role="alert">
              {dateValidation}
            </div>
          )}

          <div className="fir-bulk-summary" aria-live="polite">
            <span>
              Area Managers selected: <strong>{selectedManagerIds.length}</strong>
            </span>
            <span>
              Sites listed: <strong>{sites.length}</strong>
            </span>
            <span>
              Date range:{' '}
              <strong>{startDate && endDate ? `${fmtDate(startDate)} – ${fmtDate(endDate)}` : 'Required'}</strong>
            </span>
            <span>
              With counts{startDate && endDate ? ' in range' : ''}: <strong>{availableSites.length}</strong>
            </span>
            <span>
              Without counts{startDate && endDate ? ' in range' : ''}: <strong>{unavailableSites.length}</strong>
            </span>
            <span>
              Selected for export: <strong>{selectedAvailableCount}</strong>
            </span>
          </div>

          <div className="fir-bulk-grid">
            <div className="fir-bulk-panel">
              <div className="fir-bulk-panel-header">
                <label>Area Managers</label>
              </div>
              {managersLoading ? (
                <p className="fir-bulk-hint">Loading Area Managers…</p>
              ) : managers.length === 0 ? (
                <p className="fir-bulk-hint">No Area Managers found for this tenant.</p>
              ) : (
                <ul className="fir-bulk-checklist">
                  {managers.map((m) => {
                    const checked = selectedManagerIds.includes(m.managerId);
                    return (
                      <li key={m.managerId}>
                        <label className="fir-bulk-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggleManager(m.managerId)}
                          />
                          <span>
                            {m.managerName || m.managerEmail || m.managerId}
                            <span className="fir-bulk-meta">
                              {m.siteCount} site{m.siteCount === 1 ? '' : 's'} · {m.sitesWithCounts} with
                              counts
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="fir-bulk-panel">
              <div className="fir-bulk-panel-header">
                <label>Sites</label>
                <div className="fir-bulk-site-actions">
                  <button
                    type="button"
                    className="fir-btn-reset"
                    onClick={onSelectAllSites}
                    disabled={!availableSites.length || sitesLoading}
                  >
                    Select all sites
                  </button>
                  <button
                    type="button"
                    className="fir-btn-reset"
                    onClick={onClearSites}
                    disabled={!selectedSiteIds.length}
                  >
                    Clear
                  </button>
                </div>
              </div>
              {!selectedManagerIds.length ? (
                <p className="fir-bulk-hint">Select one or more Area Managers to list their sites.</p>
              ) : sitesLoading ? (
                <p className="fir-bulk-hint">Loading sites…</p>
              ) : sites.length === 0 ? (
                <p className="fir-bulk-hint">No sites found for the selected Area Managers.</p>
              ) : (
                <>
                  {startDate && endDate && availableSites.length === 0 && (
                    <p className="fir-bulk-hint">
                      No counts found in the selected date range for these sites.
                    </p>
                  )}
                  <ul className="fir-bulk-checklist">
                    {sites.map((s) => {
                      const checked = selectedSiteIds.includes(s.siteId);
                      const disabled = !s.countAvailable;
                      return (
                        <li key={s.siteId} className={disabled ? 'fir-bulk-disabled' : undefined}>
                          <label className="fir-bulk-check">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => onToggleSite(s.siteId, s.countAvailable)}
                            />
                            <span>
                              {s.siteName}
                              {s.buCode ? ` — ${s.buCode}` : ''}
                              {s.isArchivedOnly ? ' (archived)' : ''}
                              <span className="fir-bulk-meta">
                                {s.managerName || '—'}
                                {s.countAvailable
                                  ? ` · ${s.countCountInRange || 0} count${s.countCountInRange === 1 ? '' : 's'}${startDate && endDate ? ' in range' : ''} · Latest: ${s.latestCount?.label || fmtDate(s.latestCount?.countDate)}`
                                  : ` · No counts${startDate && endDate ? ' in selected date range' : ''}`}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkExportFullCounts;
